import Highcharts, { Options, SeriesLineOptions } from 'highcharts/highstock';
import { useEffect, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import {
    connectedColor,
    disconnectedColor,
} from 'store/services/device-status';
import { ConnectivityTrendGraphSeries } from 'types';
import { format } from 'date-fns';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';

export interface ConnectivityTrendGraphProps {
    highcharts?: typeof Highcharts;
    connectivityTrendGraph?: ConnectivityTrendGraphSeries;
}

const defaultOptions: Options = {
    title: { text: 'Connectivity Trend' },
    chart: {
        type: 'line',
        height: 300,
    },
    tooltip: {
        split: true,
        formatter: function () {
            // The first returned item is the x value timestamp, subsequent items are the
            // y data points
            return [
                this.x
                    ? '<b>' +
                      format(new Date(this.x), 'M/d/yyyy h:mm aaa') +
                      '</b>'
                    : '',
            ].concat(
                this.points
                    ? this.points.map(function (point) {
                          return point.series.name + ': ' + point.y + '%';
                      })
                    : []
            );
        },
    },
    xAxis: {
        title: { text: 'Date' },
        type: 'datetime',
        ordinal: false,
    },
    yAxis: {
        max: 100,
        title: { text: '%' },
        labels: {
            format: '{value}%',
        },
    },
    credits: { enabled: false },
    rangeSelector: {
        selected: 1,
        buttonSpacing: 5,
        buttonTheme: {
            width: 80,
        },
        enabled: true,
        allButtonsEnabled: true,
        buttons: [
            {
                type: 'month',
                count: 1,
                text: '1 month',
            },
            {
                type: 'month',
                count: 3,
                text: '3 months',
            },
            {
                type: 'month',
                count: 6,
                text: '6 months',
            },
            {
                type: 'ytd',
                text: 'Year to date',
            },
            {
                type: 'year',
                count: 1,
                text: '1 year',
            },
            {
                type: 'year',
                count: 2,
                text: '2 years',
            },
            {
                type: 'all',
                text: 'Show All',
            },
        ],
    },
    series: [],
};

export const ConnectivityTrendGraph = ({
    highcharts,
    connectivityTrendGraph,
}: ConnectivityTrendGraphProps) => {
    const [options, setOptions] = useState<Options>({});
    const { ui } = useUi();

    // if anyone can figure out how to enable accessibility for Highcharts in TypeScript, please do so
    Highcharts.defaultOptions.accessibility = {
        enabled: false,
    };

    if (!!highcharts) {
        highcharts.defaultOptions.accessibility = {
            enabled: false,
        };
    }

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    useEffect(() => {
        if (connectivityTrendGraph) {
            const updatedSeries: SeriesLineOptions[] = [];

            // Disconnected needs to be first in order for the disconnected area portion to render on top of the connected portion.
            updatedSeries.push({
                id: 'disconnected',
                name: 'Disconnected',
                color: disconnectedColor,
                type: 'line',
                data: connectivityTrendGraph.disconnected.data,
            });

            updatedSeries.push({
                id: 'connected',
                name: 'Connected',
                color: connectedColor,
                type: 'line',
                data: connectivityTrendGraph.connected.data,
            });

            defaultOptions.series = updatedSeries;
            setOptions(defaultOptions);
        }
    }, [connectivityTrendGraph]);

    return (
        <div data-testid="ConnectivityTrendGraph">
            <HighchartsReact
                highcharts={highcharts ?? Highcharts}
                options={options}
            />
        </div>
    );
};
